import React from 'react';
import moment from 'moment';
import pluralize from 'pluralize';
import classnames from 'classnames';
import lodashCompact from 'lodash/fp/compact';

import {
  DATA_TABLE_SOURCE_PARTNER_LISTINGS_TABLE,
  DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_TABLE,
} from 'common/dataTableConstants';

import {
  LISTING_STATUS_ACTIVE,
  LISTING_STATUS_PENDING,
  LISTING_PHOTO_DIMENSION_THUMBNAIL,
} from 'common/listingConstants';

import firstWordCapitalized from 'common/util/firstWordCapitalized';

import {
  isPreLeaseActive,
  getPreLeaseDaysOnMarket,
  getMoveInReadyDaysOnMarket,
} from 'common/preLeaseHelpers';

import { getListingManagedPhotoUrls } from 'common/listingHelpers';

import { formatPrice, getFormattedFromNowDate } from 'common/formatters';

import { SR_TABLE_THEME_PARTNER } from 'client/components/SrTable';
import SrLink from 'client/components/SrLink/SrLink';
import SrDataTableUI from 'client/components/SrDataTableUI';
import SrDataTableConnected from 'client/components/SrDataTableConnected';

import './SrParnterListingsDataTable.less';

function getDataTableSettingsFromProps(props) {
  const rootViewId = props.partnerListingsViewId || DATA_TABLE_VIEW_ID_PARTNER_LISTINGS_TABLE;
  const marketIds =
    props.currentUser?.settings?.partnerPortal?.activeListings?.[props.partner.slug + '_marketIds']; // eslint-disable-line max-len
  const propertyManagerUserIds =
    props.currentUser?.settings?.partnerPortal?.activeListings?.[
      props.partner.slug + '_propertyManagerUserIds'
    ]; // eslint-disable-line max-len

  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS_TABLE,
    rootViewId: rootViewId,
    viewId: `${rootViewId}_${props.partner.id}`,
    initialViewSettings: {
      filter: {
        partnerId: props.partner.id,
        status: [LISTING_STATUS_ACTIVE, LISTING_STATUS_PENDING],
      },
      customParams: {
        partnerListingsTable: {
          partnerId: props.partner.id,
          status: [LISTING_STATUS_ACTIVE, LISTING_STATUS_PENDING],
        },
      },
      loadAllData: false,
    },
    itemsPropName: 'listings',
    count: 25,
  };

  if (marketIds && marketIds.length > 0) {
    dataTableSettings.initialViewSettings.filter.marketId = marketIds;
    dataTableSettings.viewId = `${dataTableSettings.viewId}_marketIds:${marketIds.join('_')}`; // eslint-disable-line max-len
  }

  if (propertyManagerUserIds && propertyManagerUserIds.length > 0) {
    dataTableSettings.initialViewSettings.filter.propertyManagerUserId = propertyManagerUserIds;
    dataTableSettings.viewId = `${
      dataTableSettings.viewId
    }_propertyManagerUserIds:${propertyManagerUserIds.join('_')}`; // eslint-disable-line max-len
  }

  return dataTableSettings;
}

const getHeaders = () => {
  return [
    {
      content: '',
      style: { width: '90px', boxSizing: 'border-box' },
    },
    {
      content: 'Address',
      dataTableFieldId: 'addressStreetDisplay',
      style: { width: '280px' },
    },
    {
      content: 'DOM',
      dataTableFieldId: 'daysOnMarket',
      style: { width: '60px' },
    },
    {
      content: (
        <span styleName="tooltip last7days">
          Last 7d
          <span styleName="text">Leads, Tours, Apps in the past 7 days.</span>
        </span>
      ),
      dataTableFieldId: 'leadsPast7DaysCount',
      style: { width: '80px' },
    },
    {
      content: (
        <span styleName="tooltip last7days">
          Feedback
          <span styleName="text">Average star rating from tours</span>
        </span>
      ),
      dataTableFieldId: 'tourRatingAvg',
      style: { width: '93px' },
    },
    {
      content: 'SunSpot',
      dataTableFieldId: 'sunSpot',
      style: { width: '90px' },
    },
    {
      content: 'Condition',
      dataTableFieldId: 'propertyConditionOverall',
      style: { width: '93px' },
    },
    {
      content: 'Tasks',
      dataTableFieldId: 'partnerTasksCount',
      style: { width: '72px' },
    },
    {
      content: '',
    },
    {
      content: '',
      style: { width: '16px' },
    },
  ].map((header) => ({
    ...header,
    sortPosition: 'right',
  }));
};

const getTags = (listing) => {
  return lodashCompact([
    listing?.activeConcessions?.length > 0 && {
      styleName: 'tag-theme-yellow',
      label: 'Concession',
    },
    listing.appInProgress && {
      styleName: 'tag-theme-purple-inverted',
      label: 'Apps Processing',
    },
    listing.appPendingLandlord && {
      styleName: 'tag-theme-purple',
      label: 'Apps Needs Review',
    },
    listing.appApproved && {
      styleName: 'tag-theme-green',
      label: 'Lease Pending',
    },
    listing.preLease &&
      isPreLeaseActive({ preLease: listing.preLease }) && {
        styleName: 'tag-theme-blue',
        label: `Pre ends ${moment(listing.preLease.endDate).format('MMM D')}`,
        isPreLease: true,
      },
  ]);
};

const SrPartnerListingStat = ({
  stat,
  statOK,
  statPreviousWeek,
  showStatDiff,
  label,
  flatLinedListing,
}) => {
  const statCountDiff = stat - statPreviousWeek;
  const showLeadsDiff = showStatDiff && !isNaN(statCountDiff) && statCountDiff !== 0;

  return (
    <span styleName="statLetter">
      {label === 'L' && flatLinedListing && (
        <span className="tooltipText">
          0 leads over a 7d period. <br />
          Price reduction needed. <br />
        </span>
      )}
      {showLeadsDiff && (
        <span styleName="tooltip stat">
          <span styleName={classnames('statNumberDiffWeek', { red: statCountDiff < 0 })}>
            {statCountDiff > 0 ? '+' : ''}
            {statCountDiff}
          </span>
          <span styleName="text">Change from the previous 7d.</span>
        </span>
      )}
      <span styleName={classnames('statNumber', { red: !statOK })}>{stat}</span>
      {label}
    </span>
  );
};

const SrPartnerListingStatsCell = ({ listing, isPreLease }) => {
  const showStatDiff = listing.daysOnMarket >= 14 && !listing.appApproved;
  const leadsOk =
    listing.daysOnMarket < 5
      ? true
      : (listing.sunSpot !== null ? listing.sunSpot >= 0.8 : listing.leadsPast7DaysCount >= 4) ||
        listing.appApproved;
  const toursOk =
    listing.toursPast7DaysCount > 0 ||
    listing.daysOnMarket < 5 ||
    isPreLease ||
    listing.appApproved;

  return (
    <div>
      <SrPartnerListingStat
        stat={listing.leadsPast7DaysCount}
        statOK={leadsOk}
        statPreviousWeek={listing.leadsPast14DaysCount}
        showStatDiff={showStatDiff}
        label="L"
        flatLinedListing={listing.pricingFlatLinedTasksCount > 0}
      />
      <SrPartnerListingStat
        stat={listing.toursPast7DaysCount}
        statOK={toursOk}
        statPreviousWeek={listing.toursPast14DaysCount}
        showStatDiff={showStatDiff}
        label="T"
      />
      <SrPartnerListingStat
        stat={listing.appsPast7DaysCount}
        statOK
        statPreviousWeek={listing.appsPast14DaysCount}
        showStatDiff={showStatDiff}
        label="A"
      />
    </div>
  );
};

const SrPartnerListingSunSpotCell = ({ listing }) => {
  const sunSpotOk =
    listing.sunSpot === null ||
    listing.daysOnMarket < 7 ||
    listing.sunSpot >= 0.8 ||
    listing.appApproved;
  const sunSpotValue =
    listing.daysOnMarket < 7
      ? 'waiting...'
      : listing.sunSpot === null
      ? '-'
      : `${parseInt(listing.sunSpot * 100, 10)}%`;

  const sunSpotCountDiff = listing.sunSpot - listing.sunSpotPast14DaysCount;
  const showSunSpotDiff =
    !isNaN(sunSpotCountDiff) && sunSpotCountDiff !== 0 && !listing.appApproved;

  if (listing.daysOnMarket < 7) {
    return (
      <div styleName="tooltip">
        <span styleName={classnames('sunSpot grey')}>{sunSpotValue}</span>
        <span styleName="text left28">
          Will show at 7 DOM. Listing needs more time to gather data.
        </span>
      </div>
    );
  }

  return (
    <div>
      <span styleName={classnames('sunSpot grey', { red: !sunSpotOk })}>
        {showSunSpotDiff && (
          <span styleName="tooltip stat">
            <span styleName={classnames('statNumberDiffWeek', { red: sunSpotCountDiff < 0 })}>
              {sunSpotCountDiff > 0 ? '+' : ''}
              {parseInt(sunSpotCountDiff * 100, 10)}%
            </span>
            <span styleName="text">Change in the past 7d.</span>
          </span>
        )}
        {sunSpotValue}
      </span>
    </div>
  );
};

const SrPartnerListingTourRatingCell = ({ listing }) => {
  const tourRatingOk = listing.tourRatingAvg !== null && listing.tourRatingAvg >= 4.6;

  const tourReviewAvgDiff = (listing.tourRatingPast14DaysAvg - listing.tourRatingAvg) * -1;
  const showTourReviewDiff =
    listing.daysOnMarket >= 14 && !isNaN(tourReviewAvgDiff) && tourReviewAvgDiff !== 0; // eslint-disable-line max-len

  return (
    <div styleName="tourRatingCell">
      {showTourReviewDiff && (
        <span styleName={classnames('statNumberDiffWeek')}>
          <span styleName="tooltip stat reviewAvg">
            <span styleName={classnames('diffValue', { red: tourReviewAvgDiff < 0 })}>
              {tourReviewAvgDiff > 0 ? '+' : ''}
              {tourReviewAvgDiff.toFixed(1)}
            </span>
            <span styleName="text">Change in the past 7d.</span>
          </span>
        </span>
      )}
      {listing.tourRatingAvg !== null && (
        <div styleName={classnames('star', { red: !tourRatingOk })} />
      )}
      <span
        styleName={classnames('statNumber', {
          red: listing.tourRatingAvg !== null && listing.tourRatingAvg < 4.6,
          grey: listing.tourRatingAvg === null,
        })}
      >
        {listing.tourRatingAvg === null ? '-' : listing.tourRatingAvg}
      </span>
    </div>
  );
};

const SrParnterListingsDataTable = ({ partner, currentUser, partnerListingsViewId }) => {
  return (
    <div>
      <SrDataTableConnected
        {...getDataTableSettingsFromProps({ partner, currentUser, partnerListingsViewId })}
      >
        {({
          listings,
          viewSettings,
          dataTableApiState,
          onInfiniteScrollWrapperRef,
          dataTableIsFullyLoaded,
        }) => {
          // eslint-disable-line max-len
          return (
            <div ref={onInfiniteScrollWrapperRef}>
              <SrDataTableUI
                sticky
                theme={SR_TABLE_THEME_PARTNER}
                dataTableViewSettings={viewSettings}
                headers={getHeaders()}
                rows={listings.map((listing) => {
                  const managedPhotoUrls = getListingManagedPhotoUrls({
                    listing: listing,
                    dimension: LISTING_PHOTO_DIMENSION_THUMBNAIL,
                    resolution: 2,
                  });
                  const tags = getTags(listing);
                  const isPreLease =
                    listing.preLease && isPreLeaseActive({ preLease: listing.preLease });
                  const missingPhotos = managedPhotoUrls.length < 10;
                  const preLeaseDaysOnMarket = listing.preLease
                    ? getPreLeaseDaysOnMarket({
                        listing: listing,
                        preLease: listing.preLease,
                      })
                    : null;
                  const moveInReadyDaysOnMarket = listing.preLease
                    ? getMoveInReadyDaysOnMarket({
                        listing: listing,
                        preLease: listing.preLease,
                      })
                    : listing.daysOnMarket;

                  return {
                    className: classnames({
                      flatLinedListing: listing.pricingFlatLinedTasksCount > 0,
                    }),
                    cells: [
                      {
                        content: (
                          <div
                            styleName={classnames('thumbnailCell', {
                              missingPhotos: missingPhotos,
                            })}
                          >
                            <div
                              styleName="thumbnail"
                              style={{ backgroundImage: `url(${managedPhotoUrls[0]})` }}
                              alt="Listing"
                            />
                            {missingPhotos && (
                              <div styleName="photosCountBox">
                                <span styleName="photosCount">
                                  Only {pluralize('Photo', managedPhotoUrls.length, true)}
                                </span>
                              </div>
                            )}
                          </div>
                        ),
                      },
                      {
                        content: (
                          <div styleName="addressBox">
                            <div styleName="fullAddress">
                              <span styleName="addressStreetDisplay">
                                {listing.addressStreetDisplay}
                              </span>{' '}
                              {listing.market?.displayName}, {listing.addressStateCode}
                            </div>
                            <div>
                              <span styleName="price">{formatPrice(listing.price)} rent</span>
                              <span styleName="tooltip">
                                <span
                                  styleName={classnames('lastPriceUpdate', {
                                    blue: listing.lastPriceUpdate
                                      ? moment().diff(listing.lastPriceUpdate.createdAt, 'days') <=
                                        5
                                      : false,
                                  })}
                                >
                                  {listing.lastPriceUpdate
                                    ? `Updated ${getFormattedFromNowDate({
                                        date: listing.lastPriceUpdate.createdAt,
                                        compact: true,
                                      })} ago`
                                    : 'No price changes'}
                                </span>
                                {listing.lastPriceUpdate && (
                                  <span styleName="text priceChange">Last price change</span>
                                )}
                              </span>
                            </div>
                          </div>
                        ),
                      },
                      {
                        content: (
                          <div styleName="daysOnMarketCell">
                            <span styleName="tooltip">
                              <span styleName="daysOnMarket">{moveInReadyDaysOnMarket}</span>
                              <span styleName="text left48">Move-in Ready days on market</span>
                            </span>
                            {listing.medianDaysOnMarket !== null && (
                              <>
                                <span styleName="tooltip">
                                  <span styleName="medianDaysOnMarket">
                                    {` vs ${listing.medianDaysOnMarket}`}
                                  </span>
                                  <span styleName="text left48">
                                    Median DOM of similar listings in the area.
                                  </span>
                                </span>
                              </>
                            )}
                            <br />
                            {preLeaseDaysOnMarket !== null && (
                              <span styleName="tooltip">
                                <span styleName="preLeaseDaysOnMarket">
                                  {`+${preLeaseDaysOnMarket} Pre`}
                                </span>
                                <span styleName="text left48">Pre-Leasing days on market</span>
                              </span>
                            )}
                          </div>
                        ),
                      },
                      {
                        content: (
                          <SrPartnerListingStatsCell listing={listing} isPreLease={isPreLease} />
                        ),
                        style: {
                          paddingBottom: '1px',
                        },
                      },
                      {
                        content: <SrPartnerListingTourRatingCell listing={listing} />,
                      },
                      {
                        content: <SrPartnerListingSunSpotCell listing={listing} />,
                      },
                      {
                        content: (
                          <div>
                            <span
                              styleName={classnames('condition grey', {
                                red:
                                  listing.propertyConditionOverall &&
                                  !['excellent', 'good'].includes(listing.propertyConditionOverall),
                              })}
                            >
                              {firstWordCapitalized(listing.propertyConditionOverall) || '-'}
                            </span>
                          </div>
                        ),
                      },
                      {
                        content: (
                          <div styleName="tasksCell">
                            {listing.cleanLawnTasksCount > 0 && (
                              <span styleName="taskBox cleanLawnTask">
                                <span styleName="number">{listing.cleanLawnTasksCount}</span>
                              </span>
                            )}
                            {listing.pricingFixTasksCount > 0 && (
                              <span styleName="taskBox pricingFixTask">
                                <span styleName="number">{listing.pricingFixTasksCount}</span>
                              </span>
                            )}
                          </div>
                        ),
                      },
                      {
                        content: (
                          <div styleName="tags">
                            {tags.length === 0
                              ? ' '
                              : tags.map(({ label, isPreLease, styleName }, index) => (
                                  <span styleName="tooltip">
                                    <span
                                      key={index} // eslint-disable-line react/no-array-index-key
                                      styleName={classnames('tag', styleName)}
                                    >
                                      {label}
                                    </span>
                                    {isPreLease && (
                                      <span styleName="text preLease">Pre-Leasing end date</span>
                                    )}
                                  </span>
                                ))}
                          </div>
                        ),
                      },
                      {
                        content: (
                          <div styleName="arrowBox">
                            <div styleName="arrow" />
                          </div>
                        ),
                      },
                    ].map((cell) => ({
                      ...cell,
                      content: (
                        <SrLink
                          className="link"
                          to={`/partner/${partner.slug}/listing/${listing.id}`}
                        >
                          {cell.content}
                        </SrLink>
                      ),
                    })),
                  };
                })}
              />
              {dataTableApiState.inProgress && (
                <div styleName="loadingBox">
                  <div className="loading-gif clearfix" />
                </div>
              )}
              {dataTableIsFullyLoaded && listings.length === 0 && (
                <div styleName="noRecordsBox">
                  <span styleName="text">No Active Homes</span>
                </div>
              )}
            </div>
          );
        }}
      </SrDataTableConnected>
    </div>
  );
};

SrParnterListingsDataTable.displayName = 'SrParnterListingsDataTable';

export default SrParnterListingsDataTable;
